import React from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { RV_CAMPSITE_BUNDLES_LANDING_PAGE } from '@/constants/urls';
import { useRouter } from '@/hooks/useRouter';
import { TRootState } from '@/redux/rootReducer';
import { getBookingData } from '@/redux/selectors/checkout/booking';
import { getCheckoutError } from '@/redux/selectors/checkout/error';
import { getQueryParams } from '@/redux/selectors/queryParams';
import { getQuoteId } from '@/redux/selectors/quote';
import { ECheckoutSteps } from '@/services/types/core/checkoutSteps';
import { getReferrer } from '@/utility/getReferrer';
import { getParamAsString } from '@/utility/queryParams';

import Header from './Header';

type THeaderContainerProps = {
  hasError?: boolean;
  isAuto?: boolean;
};

const HeaderContainer = ({ hasError, isAuto }: THeaderContainerProps) => {
  const router = useRouter();
  const intl = useIntl();
  const listingSlug = useSelector<TRootState, string | undefined>(
    state => state.listing.data?.slug,
  );
  const queryParams = useSelector(getQueryParams);
  const booking = useSelector(getBookingData);
  const quoteId = useSelector(getQuoteId);
  const quote = quoteId || queryParams.quote;
  const previousPageUrl =
    typeof window !== 'undefined'
      ? window.sessionStorage.getItem('previousPageUrl') || getReferrer() || null
      : null;

  const pathToCheck = RV_CAMPSITE_BUNDLES_LANDING_PAGE;

  const bundledListingPageUrl = booking
    ? `${RV_CAMPSITE_BUNDLES_LANDING_PAGE}/${booking?.deliverable_campground?.vacation_package_slug?.split('/').pop()}`
    : null;

  let isVacationPackagePage = false;

  if (typeof previousPageUrl === 'string' && previousPageUrl.trim()) {
    try {
      const parsedUrl = new URL(previousPageUrl);
      isVacationPackagePage = parsedUrl.pathname.startsWith(pathToCheck);
    } catch {
      // do nothing
    }
  }

  if (booking?.id && 'sibling_booking_group_id' in booking) {
    isVacationPackagePage = true;
  }

  let listingUrl = quote
    ? `${isAuto ? '/auto' : ''}${listingSlug}?quote=${quote}`
    : `${isAuto ? '/auto' : ''}${listingSlug}`;

  if (isVacationPackagePage) {
    listingUrl = bundledListingPageUrl || previousPageUrl || listingUrl;
  }

  const step = getParamAsString(router.query.step);
  const bookingError = useSelector(getCheckoutError);

  // TODO: Integrate with API's steps
  const backLinkLabelByStep: Record<string, string> = {
    [ECheckoutSteps.NEW]: intl.formatMessage({
      defaultMessage: 'Back to listing page',
      id: '//Gljv',
      description: 'Checkout > Header > back link title',
    }),
    [ECheckoutSteps.CONTACT]: intl.formatMessage({
      defaultMessage: 'Back to listing page',
      id: '//Gljv',
      description: 'Checkout > Header > back link title',
    }),
    [ECheckoutSteps.QUESTIONS]: intl.formatMessage({
      defaultMessage: 'Back to Booking Request',
      id: 'E6Y3WF',
      description: 'Checkout > Header > back link title',
    }),
    [ECheckoutSteps.PAYMENT]: intl.formatMessage({
      defaultMessage: 'Back to Booking Request',
      id: 'E6Y3WF',
      description: 'Checkout > Header > back link title',
    }),
  };

  return (
    <Header
      key={String(bookingError)}
      hasError={hasError}
      backLinkUrl={listingUrl}
      backLinkTitle={step ? backLinkLabelByStep[step] : undefined}
      isAuto={isAuto}
    />
  );
};

export default HeaderContainer;
